/**
 * The `author` a comment is written with (RFC 0324 3a).
 *
 * Every place the window writes a comment calls this instead of writing the
 * literal `'user'`. The first time, it asks for a name; after that it is one
 * storage read. It also puts the person in the project, so everyone reading
 * the project can name the comment's writer.
 */

import type { Repo } from '@automerge/automerge-repo'
import { type PersonEntry, upsertPerson } from '@mod/core/model'
import { readLocalPerson } from './localPerson'

/** What a comment is written as when the person declined to give a name:
 *  the same string every comment carried before people existed. */
export const ANONYMOUS_AUTHOR = 'user'

export async function commentAuthor(
  repo: Repo,
  projectId: string,
  ask: () => Promise<PersonEntry | null> = askForNameLazily,
): Promise<string> {
  const person = readLocalPerson() ?? (await ask())
  if (!person) return ANONYMOUS_AUTHOR
  try {
    await upsertPerson(repo, projectId, person)
  } catch {
    // The comment matters more than the name: write it even if the people
    // list could not be updated, and try again on the next comment.
  }
  return person.id
}

/** The prompt is React; loaded only when somebody actually needs it. */
async function askForNameLazily(): Promise<PersonEntry | null> {
  const { askForName } = await import('./NamePrompt')
  return askForName()
}

/** Someone who declined to give a name is not asked again on this page. */
let declinedThisPage = false

/**
 * Who a typed change is written as (RFC 0313 3a): the same person a comment is
 * written as, asked the same way, with the prompt filled in from `gh` on the
 * desktop. Asked when an editor opens rather than mid-sentence, and only once
 * per page if declined, after which typing is unattributed.
 */
export async function editorAuthor(
  repo: Repo,
  projectId: string,
): Promise<string | null> {
  if (declinedThisPage && !readLocalPerson()) return null
  const id = await commentAuthor(repo, projectId)
  if (id === ANONYMOUS_AUTHOR) {
    declinedThisPage = true
    return null
  }
  return id
}
